import { ref } from 'vue'
import { invoke, isTauri } from '@tauri-apps/api/core'
import { listen } from '@tauri-apps/api/event'
import { BrowserSynthEngine } from '../audio/synth/BrowserSynthEngine.js'
import { useAudioStore, AUDIO_SYNTH_RESOURCE_TYPE, AUDIO_URL_RESOURCE_TYPE, validateAudioStreamUrl } from '../stores/audioStore.js'
import { useSynthStore } from '../stores/synthStore.js'

/** The Designer has one preview player, shared by local and external audio. */
export const activeAudioPreviewUuid = ref(null)
export const audioPreviewError = ref(null)

let transition = Promise.resolve()
let localAudio = null
let nativePreviewActive = false
let nativeListenersPromise = null
let synthPlaybackEngine = null
let synthMonitorTimer = null

function ensureNativeListeners() {
  if (!nativeListenersPromise) {
    nativeListenersPromise = Promise.all([
      listen('audio-preview-ended', event => {
        if (event.payload?.previewId === activeAudioPreviewUuid.value) {
          nativePreviewActive = false
          activeAudioPreviewUuid.value = null
        }
      }),
      listen('audio-preview-error', event => {
        if (event.payload?.previewId === activeAudioPreviewUuid.value) {
          nativePreviewActive = false
          audioPreviewError.value = event.payload?.message || 'Audio preview failed.'
          activeAudioPreviewUuid.value = null
        }
      }),
    ]).catch(error => {
      nativeListenersPromise = null
      throw error
    })
  }
  return nativeListenersPromise
}

function serialize(operation) {
  const next = transition.then(operation, operation)
  transition = next.catch(() => {})
  return next
}

async function stopCurrentPreview() {
  audioPreviewError.value = null
  if (synthMonitorTimer != null) {
    clearInterval(synthMonitorTimer)
    synthMonitorTimer = null
  }
  if (synthPlaybackEngine) {
    const engine = synthPlaybackEngine
    synthPlaybackEngine = null
    engine.stop()
    engine.dispose()
  }
  if (localAudio) {
    const audio = localAudio
    localAudio = null
    audio.pause()
    audio.removeAttribute('src')
    audio.load()
  }

  if (nativePreviewActive) {
    nativePreviewActive = false
    await invoke('audio_preview_stop_command')
  }
  activeAudioPreviewUuid.value = null
}

/** Stop whichever local-file or native web-stream preview is active. */
export function stopAudioPreview() {
  return serialize(stopCurrentPreview)
}

/** Play a library resource through its provider, after cleaning up the old one. */
export function previewAudio(resource) {
  return serialize(async () => {
    await stopCurrentPreview()
    if (!resource) return

    if (resource.type === AUDIO_URL_RESOURCE_TYPE) {
      if (!isTauri()) {
        throw new Error('External audio preview is available in the Desktop app.')
      }
      const url = validateAudioStreamUrl(resource.url)
      await ensureNativeListeners()
      nativePreviewActive = true
      activeAudioPreviewUuid.value = resource.uuid
      try {
        await invoke('audio_preview_start_command', {
          url,
          mimeType: resource.mimeType ?? null,
          previewId: resource.uuid,
        })
      } catch (error) {
        nativePreviewActive = false
        activeAudioPreviewUuid.value = null
        throw error
      }
    } else if (resource.type === AUDIO_SYNTH_RESOURCE_TYPE) {
      const synth = useSynthStore().getSynth(resource.synthId)
      if (!synth) throw new Error(`This Audio Resource references a missing synth (${resource.synthId}).`)

      const engine = new BrowserSynthEngine()
      synthPlaybackEngine = engine
      try {
        await engine.play(synth)
      } catch (error) {
        if (synthPlaybackEngine === engine) synthPlaybackEngine = null
        engine.dispose()
        throw error
      }
      synthMonitorTimer = setInterval(() => {
        if (synthPlaybackEngine !== engine || engine.isPlaying()) return
        clearInterval(synthMonitorTimer)
        synthMonitorTimer = null
        synthPlaybackEngine = null
        engine.dispose()
        if (activeAudioPreviewUuid.value === resource.uuid) activeAudioPreviewUuid.value = null
      }, 100)
    } else {
      const source = await useAudioStore().getAudioDataURL(resource.uuid)
      if (!source) throw new Error('This audio resource has no local audio data.')

      const audio = new Audio(source)
      localAudio = audio
      audio.addEventListener('ended', () => {
        if (localAudio === audio) void stopAudioPreview().catch(() => {})
      }, { once: true })
      try {
        await audio.play()
      } catch (error) {
        if (localAudio === audio) localAudio = null
        audio.pause()
        audio.removeAttribute('src')
        audio.load()
        throw error
      }
    }

    activeAudioPreviewUuid.value = resource.uuid
  })
}
